package xim.poc.game.configuration.v0.tactics.behaviors

import xim.poc.game.ActorState
import xim.poc.game.CombatBonusAggregate
import xim.poc.game.StatusEffect
import xim.poc.game.configuration.ActorDamagedContext
import xim.poc.game.configuration.SkillApplierHelper
import xim.poc.game.configuration.constants.*
import xim.poc.game.configuration.v0.tactics.TacticsInstincts
import xim.poc.game.configuration.v0.tactics.addInstinct
import xim.poc.game.configuration.v0.tactics.getUnit
import xim.poc.game.event.AttackDamageType
import xim.poc.game.event.Event
import xim.util.addInPlace
import kotlin.math.pow

private enum class MetusForm(val appearance: Int, val statusEffect: StatusEffect?) {
    None(appearance = 0, statusEffect = null),
    PhysicalShield(appearance = 1, statusEffect = StatusEffect.PhysicalShield),
    MagicalShield(appearance = 2, statusEffect = StatusEffect.MagicShield),
}

private class FormState(
    val form: MetusForm,
    val damage: MutableMap<AttackDamageType, Int> = HashMap(),
    var skillCount: Int = 0,
) {
    fun totalDamage() = damage.values.sum()
}

class TacticsMetusBehavior(actorState: ActorState): TacticsBehaviorController(actorState) {

    companion object {
        val autoAttackSkill = mskillPteraketosAutoAttack_2611
        const val formSwitchThreshold = 300
    }

    private var currentForm = FormState(MetusForm.None)
    private var previousSkill: SkillId? = null

    override fun onBehaviorInitialized(): List<Event> {
        actorState.getUnit().addInstinct(TacticsInstincts.resistAll, potency = 30)

        return emptyList()
    }

    override fun tacticsUpdate(elapsedFrames: Float): List<Event> {
        val shieldStatus = currentForm.form.statusEffect
        if (shieldStatus == null) {
            actorState.expireStatusEffect(MetusForm.PhysicalShield.statusEffect)
            actorState.expireStatusEffect(MetusForm.MagicalShield.statusEffect)
        } else {
            val shield = actorState.getOrGainStatusEffect(shieldStatus)
            shield.displayCounter = true
            shield.counter = (formSwitchThreshold - currentForm.totalDamage()).coerceAtLeast(0)
        }

        return emptyList()
    }

    override fun applyTacticsBehaviorBonuses(aggregate: CombatBonusAggregate) {
        aggregate.knockBackResistance += 100

        when (currentForm.form) {
            MetusForm.None -> {}
            MetusForm.PhysicalShield -> aggregate.physicalDamageTaken -= 1000
            MetusForm.MagicalShield -> aggregate.magicalDamageTaken -= 1000
        }
    }

    override fun getSkills(): List<SkillId> {
        return super.getSkills() + autoAttackSkill
    }

    override fun getSelectableSkills(): List<SkillId> {
        val desiredForm = getDesiredForm()

        return if (desiredForm == MetusForm.PhysicalShield) {
            listOf(mskillWheelofImpregnability_1248)
        } else if (desiredForm == MetusForm.MagicalShield) {
            listOf(mskillBastionofTwilight_1249)
        } else if (previousSkill == mskillWheelofImpregnability_1248) {
            listOf(mskillSealofQuiescence_1251)
        } else if (previousSkill == mskillBastionofTwilight_1249) {
            listOf(mskillWindsofOblivion_1250)
        } else if (previousSkill != autoAttackSkill) {
            listOf(autoAttackSkill)
        } else if (currentForm.skillCount >= 4) {
            listOf(mskillCensure_3489, mskillOsmoticWave_3490)
        } else {
            listOf(mskillMalevolentBlessing_1244, mskillPestilentPenance_1245, mskillEmptySalvation_1246, mskillInfernalDeliverance_1247)
        }
    }

    override fun estimateSkillValue(query: TacticsSkillQuery): Float {
        if (query.skill == mskillWheelofImpregnability_1248 || query.skill == mskillBastionofTwilight_1249) {
            return 1f
        }

        val distanceMultiplier = (1/100f).pow(query.destinationPosition.distance(query.originalPosition))

        if (query.skill == mskillWindsofOblivion_1250 || query.skill == mskillSealofQuiescence_1251) {
            return distanceMultiplier * query.result.allTargetIds.size.toFloat()
        }

        return distanceMultiplier * super.estimateSkillValue(query)
    }

    override fun onSkillExecutedScoped(primaryTargetContext: SkillApplierHelper.TargetEvaluatorContext): List<Event> {
        if (primaryTargetContext.skill != autoAttackSkill) { currentForm.skillCount += 1 }
        previousSkill = primaryTargetContext.skill

        if (primaryTargetContext.skill == mskillWheelofImpregnability_1248) {
            switchForm(MetusForm.PhysicalShield)
        }

        if (primaryTargetContext.skill == mskillBastionofTwilight_1249) {
            switchForm(MetusForm.MagicalShield)
        }

        return emptyList()
    }

    override fun onBehaviorDamaged(context: ActorDamagedContext): List<Event> {
        currentForm.damage.addInPlace(context.damageType, context.damageAmount)

        if (currentForm.form != MetusForm.None && currentForm.totalDamage() > formSwitchThreshold) {
            switchForm(MetusForm.None)
        }

        return emptyList()
    }

    private fun switchForm(form: MetusForm) {
        currentForm = FormState(form)
        actorState.appearanceState = form.appearance
    }

    private fun getDesiredForm(): MetusForm {
        if (currentForm.form != MetusForm.None || currentForm.totalDamage() < formSwitchThreshold) { return MetusForm.None }
        val maxDamageType = currentForm.damage.maxBy { it.value }.key

        return when (maxDamageType) {
            AttackDamageType.Physical -> MetusForm.PhysicalShield
            AttackDamageType.Magical -> MetusForm.MagicalShield
            else -> listOf(MetusForm.PhysicalShield, MetusForm.MagicalShield).random()
        }
    }

}